import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getAccount, getTickets, createAccountContact, getClientMeetings } from '@/services/api';
import { TICKET_STATUS_LABELS, TICKET_PRIORITY_LABELS } from '@/types';
import type { TicketStatus, TicketPriority } from '@/types';
import AccountContactList from './AccountContactList';
import { ContactForm } from '../../components/forms/ContactForm';
import type { ContactFormData } from '../../components/forms/ContactForm';
import { useState } from 'react';
import { useToast } from '../../contexts/ToastContext';
import { listProspects } from '@/services/prospectsApi';
import { PROSPECT_STATUS_LABELS, type ProspectListItem } from '@/types/prospect';
import {
  CalendarDaysIcon,
  LinkIcon,
  PencilSquareIcon,
  UserPlusIcon,
} from '@heroicons/react/24/outline';

function formatMeetingTooltip(meeting: { eventName: string | null; scheduledStartUtc: string; scheduledTimezone: string | null }) {
  const scheduledAt = new Date(meeting.scheduledStartUtc).toLocaleString();
  const zone = meeting.scheduledTimezone ? ` (${meeting.scheduledTimezone})` : '';
  return `Next meeting: ${meeting.eventName ?? 'Meeting'} at ${scheduledAt}${zone}`;
}

export default function ClientProfile() {
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const toast = useToast();
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);

  const { data: account, isLoading: accountLoading } = useQuery({
    queryKey: ['account', id],
    queryFn: () => getAccount(id!),
    enabled: !!id,
  });

  const { data: ticketsPage } = useQuery({
    queryKey: ['tickets', { accountId: id }],
    queryFn: () => getTickets({ accountId: id }),
    enabled: !!id,
  });

  const { data: meetingsPage } = useQuery({
    queryKey: ['meetings', 'client', id],
    queryFn: () => getClientMeetings(id!, { pageSize: 50 }),
    enabled: !!id,
  });

  const { data: prospectsPage } = useQuery({
    queryKey: ['prospects', 'client-profile', id, account?.companyName],
    queryFn: () =>
      listProspects({
        pageNumber: 1,
        pageSize: 100,
        sortBy: 'updatedAt',
        sortDir: 'desc',
        'filter[companyName]': account?.companyName ?? undefined,
      }),
    enabled: !!id && !!account?.companyName,
  });

  const createContactMutation = useMutation({
    mutationFn: (data: ContactFormData) => createAccountContact(id!, data),
    onSuccess: (newContact) => {
      queryClient.invalidateQueries({ queryKey: ['accountContacts', id] });
      queryClient.invalidateQueries({ queryKey: ['account', id] });
      setIsContactModalOpen(false);
      
      // Check for warnings
      if (newContact.warnings && newContact.warnings.length > 0) {
        newContact.warnings.forEach(warning => {
          toast.error(warning.message);
        });
      } else {
        toast.success('Contact created successfully');
      }
    },
    onError: (error: any) => {
      const message = error.response?.data?.error || error.message || 'Failed to create contact';
      toast.error(message);
    },
  });

  const handleCreateContact = async (data: ContactFormData) => {
    await createContactMutation.mutateAsync(data);
  };

  if (accountLoading) return <p className="text-brand-text-secondary">Loading…</p>;
  if (!account) return <p className="text-red-500">Account not found.</p>;

  const tickets = ticketsPage?.items ?? [];
  const meetings = meetingsPage?.items ?? [];
  const prospectsForAccount: ProspectListItem[] = (prospectsPage?.items ?? []).filter(
    (prospect) => prospect.accountId === account.accountId,
  );
  const now = Date.now();
  const nextMeeting = meetings
    .filter((meeting) => meeting.meetingStatus !== 'Canceled' && Date.parse(meeting.scheduledStartUtc) >= now)
    .sort((a, b) => Date.parse(a.scheduledStartUtc) - Date.parse(b.scheduledStartUtc))[0];

  const leadProspectItems = (() => {
    const leads = (account.associatedLeads ?? []).map((lead) => ({
      id: lead.leadId,
      href: `/leads/${lead.leadId}`,
      subject: lead.subject || 'Untitled Lead',
      status: lead.status,
      createdAt: lead.createdAt,
      kind: 'Lead' as const,
    }));

    const prospects = prospectsForAccount.map((prospect) => ({
      id: prospect.prospectId,
      href: `/prospects/${prospect.prospectId}`,
      subject: prospect.subject || prospect.leadInternalId || 'Untitled Prospect',
      status: PROSPECT_STATUS_LABELS[prospect.status] ?? String(prospect.status),
      createdAt: prospect.createdAt,
      kind: 'Prospect' as const,
    }));

    return [...leads, ...prospects].sort(
      (a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt),
    );
  })();

  return (
    <div className="mx-auto max-w-[50rem]">
      <div className="flex items-center justify-between mb-4">
        {/* Updated: "Account Profile" per feature 041 */}
        <h1 className="text-2xl font-semibold">Account Profile</h1>
        <div className="flex items-center gap-2">
          <Link
            to={`/clients/${account.accountId}/edit`}
            title="Edit Client"
            aria-label="Edit Client"
            className="rounded-md border border-brand-border bg-brand-surface-card p-2 text-brand-text-primary transition hover:bg-brand-surface"
          >
            <PencilSquareIcon className="h-5 w-5" title="Edit Client" />
          </Link>
          <Link
            to={`/clients/${account.accountId}/edit#partner-association`}
            title="Associate with Partner"
            aria-label="Associate with Partner"
            className="rounded-md border border-brand-border bg-brand-surface-card p-2 text-brand-text-primary transition hover:bg-brand-surface"
          >
            <LinkIcon className="h-5 w-5" title="Associate with Partner" />
          </Link>
        </div>
      </div>

      <div className="bg-white rounded shadow p-4 mb-6 grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
        <div><span className="text-brand-text-secondary">ID:</span> <span className="font-mono">{account.accountId}</span></div>
        <div><span className="text-brand-text-secondary">Company:</span> <strong>{account.companyName ?? '—'}</strong></div>
        {(account.firstName || account.lastName) && (
          <div>
            <span className="text-brand-text-secondary">Contact Name:</span>{' '}
            <strong>{[account.firstName, account.lastName].filter(Boolean).join(' ')}</strong>
          </div>
        )}
        <div><span className="text-brand-text-secondary">Email:</span> {account.email ?? '—'}</div>
        <div><span className="text-brand-text-secondary">Phone:</span> {account.phone ?? '—'}</div>
        {/* T095: Display formatted Address field from backend */}
        <div><span className="text-brand-text-secondary">Address:</span> {account.address ?? '—'}</div>
        {account.websiteUrl && (
          <div>
            <span className="text-brand-text-secondary">Website:</span>{' '}
            <a href={account.websiteUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline break-all">
              {account.websiteUrl}
            </a>
          </div>
        )}
        <div>
          <span className="text-brand-text-secondary">Status:</span>{' '}
          <span className={`text-xs px-2 py-0.5 rounded-full ${account.status === 'Active' ? 'bg-brand-accent/15 text-brand-accent' : 'bg-gray-100 text-gray-500'}`}>
            {account.status}
          </span>
        </div>
        <div>
          <span className="text-brand-text-secondary">Meetings:</span>{' '}
          <span
            className="relative inline-flex align-middle"
            title={nextMeeting ? formatMeetingTooltip(nextMeeting) : 'No upcoming meetings for this account'}
            aria-label={nextMeeting ? formatMeetingTooltip(nextMeeting) : 'No upcoming meetings for this account'}
          >
            <CalendarDaysIcon
              className="h-5 w-5 text-brand-text-secondary"
              title={nextMeeting ? formatMeetingTooltip(nextMeeting) : 'No upcoming meetings for this account'}
            />
            {nextMeeting && (
              <span className="absolute -right-0.5 -top-0.5 h-2 w-2 rounded-full bg-emerald-500" aria-hidden="true" />
            )}
          </span>
        </div>
      </div>

      <div className="flex justify-between items-center mb-2">
        <h2 className="text-lg font-semibold">Contacts ({account.contactCount})</h2>
        <button
          type="button"
          onClick={() => setIsContactModalOpen(true)}
          title="Add Contact"
          aria-label="Add Contact"
          className="rounded-md border border-brand-border bg-brand-surface-card p-2 text-brand-text-primary transition hover:bg-brand-surface"
        >
          <UserPlusIcon className="h-5 w-5" title="Add Contact" />
        </button>
      </div>
      <div className="mb-6">
        <AccountContactList accountId={account.accountId} />
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <section>
          <h2 className="mb-2 text-lg font-semibold">Lead/Prospects</h2>
          {leadProspectItems.length === 0 && (
            <p className="text-brand-text-secondary text-sm">No leads or prospects.</p>
          )}
          {leadProspectItems.length > 0 && (
            <ul className="space-y-2">
              {leadProspectItems.map((item) => (
                <li key={`${item.kind}-${item.id}`} className="bg-white rounded shadow px-4 py-3 text-sm flex items-center gap-3">
                  <span className="rounded bg-brand-surface px-2 py-0.5 text-xs text-brand-text-secondary">{item.kind}</span>
                  <Link to={item.href} className="text-brand-text-primary hover:underline font-medium flex-1">
                    {item.subject}
                  </Link>
                  <span className="text-brand-text-secondary">{item.status}</span>
                  <span className="text-brand-text-secondary">{new Date(item.createdAt).toLocaleDateString()}</span>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section>
          <h2 className="mb-2 text-lg font-semibold">Tickets</h2>
          {tickets.length === 0 && <p className="text-brand-text-secondary text-sm">No tickets.</p>}
          {tickets.length > 0 && (
            <ul className="space-y-2">
              {tickets.map((t) => (
                <li key={t.ticketId} className="bg-white rounded shadow px-4 py-3 text-sm flex justify-between items-center gap-3">
                  <Link to={`/tickets/${t.ticketId}`} className="text-brand-text-primary hover:underline font-medium flex-1">
                    {t.subject || 'Untitled Ticket'}
                  </Link>
                  <span>{TICKET_STATUS_LABELS[t.status as TicketStatus]}</span>
                  <span className="text-brand-text-secondary">{TICKET_PRIORITY_LABELS[t.priority as TicketPriority]}</span>
                  <span className="text-brand-text-secondary">{new Date(t.createdAt).toLocaleDateString()}</span>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>

      {/* Create Contact Modal */}
      {isContactModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            {/* Background overlay */}
            <div 
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" 
              aria-hidden="true"
              onClick={() => setIsContactModalOpen(false)}
            ></div>

            {/* Center modal */}
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

            {/* Modal panel */}
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                    <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4" id="modal-title">
                      Add New Contact
                    </h3>
                    <ContactForm
                      onSubmit={handleCreateContact}
                      onCancel={() => setIsContactModalOpen(false)}
                      isLoading={createContactMutation.isPending}
                      submitLabel="Create Contact"
                      mode="create"
                      accountStatus={account.status}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
